import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthAudience, AuthenticatedUser, Role } from '../lib/auth/types';
import { signIn as doSignIn, signInDev as doSignInDev, signOut as doSignOut, getAuthenticatedUser } from '../lib/auth/tokenManager';
import { getStoredTokens, subscribeToTokenChanges } from '../lib/auth/tokenStorage';
import { useTokenRefresh } from '../lib/auth/useTokenRefresh';

// Role hierarchy for "view-as": Admin can simulate any role, User can
// simulate User or Entrant, Entrant only themselves. Frontend-only — the
// backend still enforces on the JWT, so a simulated Entrant super-admin
// still has Admin-level API access. Purely a UI preview aid.
// Only used to order the "view as" picker (highest-privilege first) among the
// roles a user actually holds. PR is a standalone read-only role; it sits low.
const ROLE_HIERARCHY: Record<Role, number> = { Admin: 4, User: 3, Entrant: 2, PR: 1 };
const VIEW_AS_STORAGE_KEY = 'waf-view-as-role';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (role: Role) => boolean;
  login: (email: string, password: string, audience?: AuthAudience) => Promise<AuthenticatedUser | null>;
  /** Local dev only: sign in as a fake user with this role, no password. */
  devLogin: (role: Role) => Promise<AuthenticatedUser | null>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** The role the user is currently simulating, or null when not simulating. */
  viewAsRole: Role | null;
  /** Pass null to stop simulating and return to real roles. */
  setViewAsRole: (role: Role | null) => void;
  /** Roles this user is allowed to view-as (their real roles + anything below). */
  availableViewAsRoles: Role[];
  /** True when view-as is active — use to render "Viewing as…" banners. */
  isSimulating: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Audience is tracked per-user so the refresh loop points to the right
  // Zitadel project. Seeded from storage so a page reload with an entrant
  // session doesn't flip back to admin on first refresh.
  const [audience, setAudience] = useState<AuthAudience>(
    () => getStoredTokens()?.audience ?? 'admin'
  );

  const refreshUser = useCallback(async () => {
    try {
      const u = await getAuthenticatedUser();
      // The admin->entrant login fallback can swap the stored tokens while this
      // is in flight. Discard a result whose audience no longer matches what's
      // stored, so an empty-roles admin pass can't clobber the entrant user.
      if (getStoredTokens()?.audience !== audience) return;
      setUser(u);
    } finally {
      setIsLoading(false);
    }
  }, [audience]);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  // Cross-tab: if another tab logged out or logged in with a different
  // audience, pick up the new audience from storage. When the audience
  // changes, the re-render produces a new refreshUser (closed over the new
  // audience) and the first effect fires refreshUser() — so we skip the
  // refresh here to avoid firing it twice with stale then fresh audience.
  useEffect(() => {
    return subscribeToTokenChanges(() => {
      const storedAud = getStoredTokens()?.audience;
      if (storedAud && storedAud !== audience) {
        setAudience(storedAud);
        return;
      }
      void refreshUser();
    });
  }, [audience, refreshUser]);

  useTokenRefresh(audience);

  // View-as simulation state. Stored in localStorage so a page reload
  // doesn't drop the simulated role mid-session. Validated against the
  // user's real roles below — a stale value from a previous account is
  // ignored rather than silently granting imagined access.
  const [viewAsRole, setViewAsRoleState] = useState<Role | null>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(VIEW_AS_STORAGE_KEY) : null;
    if (stored === 'Admin' || stored === 'User' || stored === 'Entrant' || stored === 'PR') return stored;
    return null;
  });

  const availableViewAsRoles = useMemo<Role[]>(() => {
    // Only roles actually present on the JWT — simulating a role the
    // token doesn't carry would break the first API call under that
    // scheme. Zitadel audiences are project-scoped, so an admin-audience
    // session naturally excludes Entrant (it lives on a separate
    // project) and vice versa. Sort highest-privilege first.
    const roles = user?.roles ?? [];
    return [...roles].sort((a, b) => ROLE_HIERARCHY[b] - ROLE_HIERARCHY[a]);
  }, [user]);

  // If the stored view-as is no longer valid for the current user
  // (different account, role removed), drop it silently.
  useEffect(() => {
    if (viewAsRole && !availableViewAsRoles.includes(viewAsRole)) {
      setViewAsRoleState(null);
      window.localStorage.removeItem(VIEW_AS_STORAGE_KEY);
    }
  }, [viewAsRole, availableViewAsRoles]);

  const setViewAsRole = useCallback((role: Role | null) => {
    if (role === null) {
      window.localStorage.removeItem(VIEW_AS_STORAGE_KEY);
    } else {
      window.localStorage.setItem(VIEW_AS_STORAGE_KEY, role);
    }
    setViewAsRoleState(role);
  }, []);

  const login = useCallback(async (email: string, password: string, aud: AuthAudience = 'admin') => {
    setIsLoading(true);
    try {
      const u = await doSignIn(email, password, aud);
      // Only flip audience once the backend accepted the creds. Failure
      // paths leave the existing audience untouched — no rollback needed
      // because we never speculatively moved it, and rolling back would
      // clobber any cross-tab update that raced this sign-in.
      setAudience(aud);
      setUser(u);
      // A fresh login ends any previous simulation; the new user might
      // not be entitled to the same roles.
      setViewAsRole(null);
      // Return so callers can inspect roles immediately (login() resolves
      // before React commits the state set above, so reading user from
      // useAuth() right after this await would see the previous value).
      return u;
    } finally {
      setIsLoading(false);
    }
  }, [setViewAsRole]);

  const devLogin = useCallback(async (role: Role) => {
    setIsLoading(true);
    try {
      const { user: u, audience: aud } = await doSignInDev(role);
      setAudience(aud);
      setUser(u);
      setViewAsRole(null);
      return u;
    } finally {
      setIsLoading(false);
    }
  }, [setViewAsRole]);

  const logout = useCallback(async () => {
    try {
      await doSignOut(audience);
    } finally {
      setUser(null);
      setViewAsRole(null);
    }
  }, [audience, setViewAsRole]);

  // When simulating, hasRole reflects the simulated role only. Real
  // roles stay on user.roles for the view-as picker itself.
  const hasRole = useCallback(
    (role: Role) => {
      if (viewAsRole !== null) return viewAsRole === role;
      return user?.roles.includes(role) ?? false;
    },
    [user, viewAsRole]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      hasRole,
      login,
      devLogin,
      logout,
      refreshUser,
      viewAsRole,
      setViewAsRole,
      availableViewAsRoles,
      isSimulating: viewAsRole !== null,
    }),
    [user, isLoading, hasRole, login, devLogin, logout, refreshUser, viewAsRole, setViewAsRole, availableViewAsRoles]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
